/**
 * Template auto-sync hook for EditSentDropModal senders.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  useCreateGatewaySenderTemplate,
  useGatewaySenderTemplates,
  useUpdateGatewaySenderTemplate,
} from '@/hooks';

export function useSentDropTemplateSync(
  countryKey: string,
  effectiveRouteId: number,
  selectedSenderNames: string[],
  setSelectedSenderNames: (fn: (prev: string[]) => string[]) => void,
  sendersList: string[],
  onAddSender?: (name: string) => Promise<void>,
) {
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const { data: senderTemplates = [] } = useGatewaySenderTemplates(countryKey, effectiveRouteId);
  const createTemplate = useCreateGatewaySenderTemplate(countryKey, effectiveRouteId);
  const updateTemplate = useUpdateGatewaySenderTemplate(countryKey, effectiveRouteId);

  useEffect(() => { setActiveTemplateId(null); }, [countryKey, effectiveRouteId]);

  const lastSyncedRef = useRef<string>('');
  useEffect(() => {
    if (!activeTemplateId || selectedSenderNames.length === 0) return;
    const sorted = [...selectedSenderNames].sort();
    const key = JSON.stringify(sorted);
    if (key === lastSyncedRef.current) return;
    if (!senderTemplates.find((x) => x.id === activeTemplateId)) return;
    const tm = setTimeout(() => {
      const first = selectedSenderNames[0] ?? 'Template';
      const name = selectedSenderNames.length > 1 ? `${first} +${selectedSenderNames.length - 1}` : first;
      updateTemplate.mutate({ id: activeTemplateId, senderNames: selectedSenderNames, name });
      lastSyncedRef.current = key;
    }, 400);
    return () => clearTimeout(tm);
  }, [activeTemplateId, selectedSenderNames, senderTemplates, updateTemplate]);

  const handleApplyTemplate = useCallback(async (senders: string[]) => {
    setSelectedSenderNames(() => senders);
    const missing = senders.filter((s) => !sendersList.includes(s));
    if (missing.length > 0 && onAddSender) {
      for (const name of missing) await onAddSender(name);
    }
  }, [sendersList, onAddSender, setSelectedSenderNames]);

  const handleSelectTemplate = useCallback(async (template: { id: string; senderNames: string[] } | null) => {
    if (!template) { setActiveTemplateId(null); return; }
    setActiveTemplateId(template.id);
    await handleApplyTemplate(template.senderNames);
  }, [handleApplyTemplate]);

  const handleSaveAsTemplate = useCallback(async () => {
    if (selectedSenderNames.length === 0 || effectiveRouteId <= 0) return;
    const first = selectedSenderNames[0] ?? 'Template';
    const name = selectedSenderNames.length > 1 ? `${first} +${selectedSenderNames.length - 1}` : first;
    await createTemplate.mutateAsync({ name, senderNames: selectedSenderNames });
  }, [selectedSenderNames, effectiveRouteId, createTemplate]);

  return { senderTemplates, activeTemplateId, handleSelectTemplate, handleSaveAsTemplate };
}
